import { ctArchiveLabel } from './ctArchiveLabel'
import type { WorkflowStep } from '../WorkflowStepProgress'
import type { DiagnocatUploadJob } from "./diagnocatTypes"

/** Server-reported upload job statuses (Young public API). */
export const DIAGNOCAT_UPLOAD_JOB_STATUSES = [
  "pending",
  "fetching",
  "uploading",
  "completed",
  "failed",
] as const

export type DiagnocatUploadJobStatus = (typeof DIAGNOCAT_UPLOAD_JOB_STATUSES)[number]

export const DIAGNOCAT_WORKFLOW_STEP_IDS = {
  fetchCt: "fetch_ct",
  upload: "upload",
  analyse: "analyse",
  findingsReady: "findings_ready",
  nasPhotosPrefetch: "nas_photos_prefetch",
  composePlan: "compose_plan",
  reviewPlan: "review_plan",
  planApplied: "plan_applied",
  presentationPhotos: "presentation_photos",
} as const

export type DiagnocatPlanComposePhase =
  | "idle"
  | "proposing"
  | "preview"
  | "applying"
  | "done"
  | "error"

export type DiagnocatNasPrefetchPhase = "idle" | "loading" | "ready" | "error"
export type DiagnocatPresentationPhase = "idle" | "active" | "done" | "error"

export type DiagnocatWorkflowSnapshot = {
  findingsCount: number
  analysesCount: number
}

export type BuildDiagnocatWorkflowStepsInput = {
  job: DiagnocatUploadJob | null
  jobPipelineActive: boolean
  snapshot: DiagnocatWorkflowSnapshot
  composePhase: DiagnocatPlanComposePhase
  composeError?: string | null
  nasPrefetchPhase?: DiagnocatNasPrefetchPhase
  nasPhotoCount?: number
  presentationPhase?: DiagnocatPresentationPhase
}

export function isDiagnocatUploadJobStatus(value: string): value is DiagnocatUploadJobStatus {
  return (DIAGNOCAT_UPLOAD_JOB_STATUSES as readonly string[]).includes(value)
}

/** Business-ready: job flag, diagnoses_count, or snapshot findings after upload. */
export function isDiagnocatAnalysisReady(
  job: DiagnocatUploadJob | null,
  snapshot: DiagnocatWorkflowSnapshot,
): boolean {
  if ((snapshot.findingsCount ?? 0) > 0 || (snapshot.analysesCount ?? 0) > 0) {
    return true
  }
  if (!job || job.status === "failed") {
    return false
  }
  if (job.analysis_ready === true) {
    return true
  }
  if (job.status === "completed" && (job.diagnoses_count ?? 0) > 0) {
    return true
  }
  if (job.status === "completed" && snapshot.findingsCount > 0) {
    return true
  }
  return false
}

export function shouldPollDiagnocatUploadJob(
  job: DiagnocatUploadJob | null,
  snapshot: DiagnocatWorkflowSnapshot,
): boolean {
  if (!job) {
    return false
  }
  if (job.status === "failed") {
    return false
  }
  if (isDiagnocatAnalysisReady(job, snapshot)) {
    return false
  }
  return true
}

export function shouldSyncDiagnocatPatient(
  job: DiagnocatUploadJob | null,
  snapshot: DiagnocatWorkflowSnapshot,
): boolean {
  return Boolean(job && job.status === "completed" && !isDiagnocatAnalysisReady(job, snapshot))
}

function ctStepStatus(
  job: DiagnocatUploadJob | null,
  jobPipelineActive: boolean,
  phase: "fetch" | "upload" | "analyse" | "findings",
  snapshot: DiagnocatWorkflowSnapshot,
): WorkflowStep["status"] {
  const analysisReady = isDiagnocatAnalysisReady(job, snapshot)

  if (!jobPipelineActive && !job) {
    if (analysisReady) {
      if (phase === "findings") return "done"
      return "skipped"
    }
    return "pending"
  }

  const status = job?.status ?? "pending"

  if (status === "failed") {
    if (phase === "fetch" || phase === "upload") {
      return "failed"
    }
    return "pending"
  }

  switch (phase) {
    case "fetch":
      if (status === "pending" || status === "fetching") return "active"
      if (status === "uploading" || status === "completed") return "done"
      return "pending"
    case "upload":
      if (status === "pending" || status === "fetching") return "pending"
      if (status === "uploading") return "active"
      if (status === "completed") return "done"
      return "pending"
    case "analyse":
      if (status !== "completed") return "pending"
      return analysisReady ? "done" : "active"
    case "findings":
      return analysisReady ? "done" : "pending"
    default:
      return "pending"
  }
}

function nasPrefetchStepStatus(
  analysisReady: boolean,
  nasPrefetchPhase: DiagnocatNasPrefetchPhase,
): WorkflowStep["status"] {
  if (!analysisReady) {
    return "pending"
  }
  if (nasPrefetchPhase === "loading") {
    return "active"
  }
  if (nasPrefetchPhase === "ready" || nasPrefetchPhase === "error") {
    return "done"
  }
  return "pending"
}

function presentationStepStatus(
  appliedStatus: WorkflowStep["status"],
  presentationPhase: DiagnocatPresentationPhase,
): WorkflowStep["status"] {
  if (appliedStatus !== "done") {
    return "pending"
  }
  if (presentationPhase === "done") {
    return "done"
  }
  if (presentationPhase === "active") {
    return "active"
  }
  if (presentationPhase === "error") {
    return "failed"
  }
  return "pending"
}

export function buildDiagnocatCtToPlanSteps(
  input: BuildDiagnocatWorkflowStepsInput,
): WorkflowStep[] {
  const {
    job,
    jobPipelineActive,
    snapshot,
    composePhase,
    composeError,
    nasPrefetchPhase = "idle",
    nasPhotoCount,
    presentationPhase = "idle",
  } = input
  const analysisReady = isDiagnocatAnalysisReady(job, snapshot)
  const failedDetail = job?.status === "failed" ? job.error_text?.trim() : undefined
  const findingsDetail =
    analysisReady && snapshot.findingsCount > 0
      ? `${snapshot.findingsCount} зубов с находками`
      : job?.diagnoses_count
        ? `${job.diagnoses_count} находок`
        : undefined

  const fetchStatus = ctStepStatus(job, jobPipelineActive, "fetch", snapshot)
  const uploadStatus = ctStepStatus(job, jobPipelineActive, "upload", snapshot)
  const analyseStatus = ctStepStatus(job, jobPipelineActive, "analyse", snapshot)
  const findingsStatus = ctStepStatus(job, jobPipelineActive, "findings", snapshot)

  let composeStatus: WorkflowStep["status"] = "pending"
  let reviewStatus: WorkflowStep["status"] = "pending"
  let appliedStatus: WorkflowStep["status"] = "pending"

  if (!analysisReady && composePhase === "idle") {
    composeStatus = "pending"
    reviewStatus = "pending"
    appliedStatus = "pending"
  } else if (analysisReady || composePhase !== "idle") {
    switch (composePhase) {
      case "idle":
        composeStatus = analysisReady ? "pending" : "skipped"
        break
      case "proposing":
        composeStatus = "active"
        break
      case "preview":
        composeStatus = "done"
        reviewStatus = "active"
        break
      case "applying":
        composeStatus = "done"
        reviewStatus = "done"
        appliedStatus = "active"
        break
      case "done":
        composeStatus = "done"
        reviewStatus = "done"
        appliedStatus = "done"
        break
      case "error":
        composeStatus = "failed"
        break
    }
  }

  const nasPrefetchStatus = nasPrefetchStepStatus(analysisReady, nasPrefetchPhase)
  const gammaPresentationStatus = presentationStepStatus(appliedStatus, presentationPhase)

  const steps: WorkflowStep[] = [
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.fetchCt,
      label: "Загрузка КТ с архива Vatech",
      detail:
        failedDetail ??
        (fetchStatus === "active" ? "Скачиваем DICOM с рабочей станции…" : undefined),
      status: fetchStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.upload,
      label: "Отправка в Diagnocat",
      detail:
        failedDetail ??
        (uploadStatus === "active" ? "Передаём снимок в Partner API…" : undefined),
      status: uploadStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.analyse,
      label: "Анализ Diagnocat (GP)",
      detail:
        analyseStatus === "active"
          ? "Ожидаем результат анализа, синхронизируем находки…"
          : undefined,
      status: analyseStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.findingsReady,
      label: "Находки готовы",
      detail: findingsDetail,
      status: findingsStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.nasPhotosPrefetch,
      label: "Подготовка фото NAS",
      detail:
        nasPrefetchPhase === "loading"
          ? "Фоновая загрузка каталога Synology…"
          : nasPrefetchPhase === "ready"
            ? `${nasPhotoCount ?? 0} фото в каталоге`
            : nasPrefetchPhase === "error"
              ? "Каталог NAS недоступен (не блокирует план)"
              : undefined,
      status: nasPrefetchStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.composePlan,
      label: "ИИ составляет план лечения",
      detail:
        composeStatus === "active"
          ? "Анализируем находки и этапность…"
          : composePhase === "error"
            ? composeError ?? "Ошибка составления"
            : undefined,
      status: composeStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.reviewPlan,
      label: "Проверка черновика",
      detail: reviewStatus === "active" ? "Подтвердите или уточните план" : undefined,
      status: reviewStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.planApplied,
      label: "План применён",
      status: appliedStatus,
    },
    {
      id: DIAGNOCAT_WORKFLOW_STEP_IDS.presentationPhotos,
      label: "Презентация Gamma",
      detail:
        presentationPhase === "done"
          ? "Презентация готова"
          : presentationPhase === "active"
            ? "Выбор фото и генерация"
            : undefined,
      status: gammaPresentationStatus,
    },
  ]

  return steps
}

export function diagnocatJobStorageKey(scopeId: string): string {
  return `diagnocat-submit-job:${scopeId}`
}
